import React, { useCallback, useState } from 'react';
import { Loader } from '@mantine/core';
import { notifyError } from '@shakkerai/pro-components';
import { translate } from '@shakkerai/util-i18n';
import { pickLocalFiles } from '@shakkerai/util-file';
import { convert2CompressedUrl } from '@shakkerai/util-image';
import { useLoadings } from '@shakkerai/pro-store';
import { getSharedData } from '@starflow/app/store';

export const ImagePreview = ({
  disabled,
  disabledTip,
  width,
  url,
  originalImageUrl,
  height,
  ghostImage,
  uploading,
  onPickImage,
}) => {
  if (uploading) {
    return (
      <div className="flex items-center justify-center w-full h-[300px] bg-black/30">
        <Loader size={18} />
      </div>
    );
  }

  if (!url && !originalImageUrl) {
    return (
      <div
        className={`flex flex-col items-center justify-center w-full h-[300px] overflow-hidden bg-[#f4f6f8] ${
          ghostImage ? 'pointer-events-none' : ''
        } relative`}
      >
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-sm text-[#adadad]">{disabledTip || translate('imgupload_placeholder')}</span>
          <button
            disabled={disabled}
            onClick={onPickImage}
            className="w-32 h-[34px] mt-3.5 bg-white border border-[#ededed] hover:border-[#cdcdcd] rounded text-[#2d2d2d] text-xs font-grotesk"
          >
            {translate('imgref_upload')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-stretch">
      <div
        className={`flex flex-1 flex-col items-center justify-center h-[300px] overflow-hidden ${
          ghostImage ? 'pointer-events-none' : ''
        } relative`}
      >
        <img src={originalImageUrl} className="w-full h-[300px] object-contain" />
        {!!originalImageUrl && (
          <div className="absolute bg-[#000] opacity-50 text-[#fff] leading-[18px] font-medium rounded-[2px] right-[8px] bottom-[8px] px-[12px] py-[4px]  flex items-center space-x-1">
            {Math.round(width)}*{Math.round(height)}
          </div>
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/45 opacity-0 transition-opacity hover:opacity-100">
          {disabled && disabledTip ? (
            <span className="text-white text-xs font-grotesk font-semibold">{disabledTip}</span>
          ) : (
            <button
              disabled={disabled}
              onClick={onPickImage}
              className="w-[150px] h-[30px] border border-white rounded text-white text-xs font-grotesk font-semibold"
            >
              {translate('imgref_upload')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const MAX_FILE_SIZE = 1024 * 1024 * 20;

export const DropImage = React.memo((props) => {
  const { onReUploadImage, generatorId, imageInfo, uploading } = props;

  const [ghostImage, setGhostImage] = useState(null);
  const [imageDraggingOver, setImageDraggingOver] = useState(false);
  const processing = useLoadings(`${generatorId}-image-input-processing`);

  const uploadSourceImage = useCallback(
    async (file) => {
      if (file.size > MAX_FILE_SIZE) {
        notifyError(translate('common_operation_failed'), translate('img_size_toast'));
        return false;
      }
      onReUploadImage?.(file);
      // const fixedSize = findBestSizeByReferenceSize(imageInfo.width, imageInfo.height, MAX_GENERATOR_SIZE);
      // updateGenerateParams(generatorId, fixedSize);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generatorId, imageInfo],
  );

  const handleDragEnter = useCallback((event) => {
    event.preventDefault();
    setImageDraggingOver(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    setImageDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((event) => {
    event.preventDefault();
    setImageDraggingOver(false);
  }, []);

  const handleDrop = useCallback(
    async (event) => {
      event.preventDefault();
      setImageDraggingOver(false);

      if (event.dataTransfer.files.length > 0) {
        const file = event.dataTransfer.files[0];

        // TODO 上传图片\类型过滤
        if (file.type.startsWith('image')) {
          uploadSourceImage(file);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [generatorId],
  );

  const handleMouseEnter = useCallback((event) => {
    // 这里处理画布内的图片拖拽到参考图
    const draggingElement = getSharedData('dragging-element');

    if (draggingElement) {
      draggingElement.top = event.nativeEvent.offsetY;
      draggingElement.left = event.nativeEvent.offsetX;
      setGhostImage(draggingElement);
      setImageDraggingOver(true);
    }
  }, []);

  const handleMouseMove = useCallback(
    (event) => {
      if (ghostImage) {
        setGhostImage((imageData) =>
          Object.assign({}, imageData, {
            top: event.nativeEvent.offsetY,
            left: event.nativeEvent.offsetX,
          }),
        );
      }
    },
    [ghostImage],
  );

  const handleMouseLeave = useCallback(() => {
    setGhostImage(null);
    setImageDraggingOver(false);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (ghostImage) {
      // 图片插入到参考图流程
      // updateControlNetImage(generatorId, controlNetId, ghostImage);
    }

    setGhostImage(null);
    setImageDraggingOver(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ghostImage, generatorId]);

  const handlePickLocalImage = useCallback(async () => {
    const [file] = await pickLocalFiles(['jpeg', 'jpg', 'png', 'webp']);

    if (file) uploadSourceImage(file);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const originalImageUrl = convert2CompressedUrl(imageInfo?.url, 400);
  const previewUrl = convert2CompressedUrl(imageInfo?.url, 400);
  const showReload = imageInfo && !processing;

  return (
    <div
      className={`group relative ${ghostImage ? ' cursor-copy' : ''}`}
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseUp={handleMouseUp}
      onDragOver={handleDragOver}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <ImagePreview
        url={previewUrl}
        sourceImageOnly={!imageInfo}
        originalImageUrl={originalImageUrl}
        uploading={uploading}
        processing={processing}
        showReload={showReload}
        ghostImage={ghostImage}
        width={imageInfo?.width}
        height={imageInfo?.height}
        highlighted={imageDraggingOver}
        onPickImage={handlePickLocalImage}
      />
    </div>
  );
});
